/**
 * Add from the field — the tech's flow (approved mockup, steps 1–4, N and D:
 * https://claude.ai/artifact/DYgYcRWa5K5pw8tBURp7fG).
 *
 *   What are you adding?   Photo (opens the camera) · Note · File
 *   Where does this go?    This job (picked) · Device · This location · This customer
 *   Which device?          one big row per device; tap one and it saves; Back
 *   Saved                  exactly where it landed, who sees it, the customer
 *                          never does; Move it; closes itself after 3 seconds
 *
 * Started from a device's own + (`presetAssetId`), the device is already
 * picked and both "where" screens are skipped. Helpers only get This job.
 * Every screen with a choice has an ⓘ.
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    Camera,
    Check,
    ChevronLeft,
    ChevronRight,
    Eye,
    FileCheck2,
    Loader2,
    Lock,
    Mic,
    StickyNote,
} from "lucide-react";
import { InfoButton } from "@/components/info-button";
import { fileToNormalizedDataUrl } from "@/lib/downscale-image";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
    FIELD_ADD_MAX_BYTES,
    fieldAddJobKey,
    kindPathOf,
    moveFieldAdd,
    removeFieldAdd,
    saveFieldAdd,
    type FieldAddItem,
    type FieldAddJobView,
    type FieldAddKind,
    type FieldTarget,
    type PickerDevice,
    type SaveResult,
} from "./field-add-api";
import { FIELD_ADD_HELP } from "./field-add-help";
import { InsideOnlyLine, PLACES, PlacePill, deviceName, shortTime, whereLine, whoSees } from "./places";

export type FieldAddStep = "what" | "note" | "where" | "device" | "saved" | "mine";

export interface FieldAddSheetProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    view: FieldAddJobView;
    isOnline: boolean;
    /** Started from a device card's +: that device is already picked. */
    presetAssetId?: string | null;
    /** Proof shots and tests: open on a given screen with given picks. */
    initial?: Partial<{
        step: FieldAddStep;
        kind: FieldAddKind;
        target: FieldTarget;
        assetId: string | null;
        body: string;
        needsOffice: boolean;
        photo: string;
        saved: SaveResult;
    }>;
    /** Tests: don't close by itself on the Saved screen. */
    noAutoClose?: boolean;
}

const KIND_WORD: Record<FieldAddKind, string> = { photo: "Photo", note: "Note", file: "File" };

export function FieldAddSheet({
    open,
    onClose,
    jobId,
    view,
    isOnline,
    presetAssetId = null,
    initial,
    noAutoClose,
}: FieldAddSheetProps) {
    const qc = useQueryClient();
    const targets = view.targets ?? ["job"];
    const devices = view.devices ?? [];
    const helperOnly = targets.length === 1;
    const presetDevice = presetAssetId ? (devices.find((d) => d.assetId === presetAssetId) ?? null) : null;

    const [step, setStep] = useState<FieldAddStep>(initial?.step ?? "what");
    const [kind, setKind] = useState<FieldAddKind>(initial?.kind ?? "photo");
    const [target, setTarget] = useState<FieldTarget>(initial?.target ?? (presetDevice ? "device" : "job"));
    const [assetId, setAssetId] = useState<string | null>(initial?.assetId ?? presetDevice?.assetId ?? null);
    const [body, setBody] = useState(initial?.body ?? "");
    const [needsOffice, setNeedsOffice] = useState(initial?.needsOffice ?? false);
    const [photo, setPhoto] = useState<string | null>(initial?.photo ?? null);
    const [file, setFile] = useState<File | null>(null);
    const [saved, setSaved] = useState<SaveResult | null>(initial?.saved ?? null);
    /** After "Move it": the where screens re-point the saved add instead of saving a new one. */
    const [moving, setMoving] = useState(false);
    const [busy, setBusy] = useState(false);
    const cameraRef = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const closeTimer = useRef<number | null>(null);

    // Fresh every time it opens (unless a proof shot set the screen).
    useEffect(() => {
        if (!open || initial) return;
        setStep("what");
        setKind("photo");
        setTarget(presetDevice ? "device" : "job");
        setAssetId(presetDevice?.assetId ?? null);
        setBody("");
        setNeedsOffice(false);
        setPhoto(null);
        setFile(null);
        setSaved(null);
        setMoving(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, presetAssetId]);

    // The Saved screen closes by itself after 3 seconds (mockup step 4).
    useEffect(() => {
        if (step !== "saved" || noAutoClose) return;
        closeTimer.current = window.setTimeout(onClose, 3000);
        return () => {
            if (closeTimer.current) window.clearTimeout(closeTimer.current);
        };
    }, [step, noAutoClose, onClose]);

    if (!open) return null;

    const mine = (view.thisJob ?? []).filter((i) => i.canChange);
    const pickedDevice: PickerDevice | null = assetId ? (devices.find((d) => d.assetId === assetId) ?? null) : null;

    async function commit(nextTarget: FieldTarget, nextAsset: string | null) {
        if (busy) return;
        setBusy(true);
        try {
            if (moving && saved?.id) {
                await moveFieldAdd(saved.kindPath, saved.id, nextTarget, nextAsset);
                setTarget(nextTarget);
                setAssetId(nextAsset);
                setMoving(false);
                setStep("saved");
            } else {
                const result = await saveFieldAdd(
                    {
                        jobId,
                        kind,
                        target: nextTarget,
                        assetId: nextTarget === "device" ? nextAsset : null,
                        needsOffice,
                        body,
                        dataUrl: photo ?? undefined,
                        file: file ?? undefined,
                    },
                    isOnline,
                );
                setTarget(nextTarget);
                setAssetId(nextAsset);
                setSaved(result);
                setStep("saved");
            }
            void qc.invalidateQueries({ queryKey: fieldAddJobKey(jobId) });
            void qc.invalidateQueries({ queryKey: ["field", "job-paperwork", jobId] });
            void qc.invalidateQueries({ queryKey: ["field", "job-notes", jobId] });
        } catch (e: unknown) {
            const err = e as { body?: { message?: string }; message?: string };
            toast.error(err?.body?.message ?? "Couldn't save. Try again.");
        } finally {
            setBusy(false);
        }
    }

    const onPhotoPicked = async (f: File | undefined) => {
        if (!f) return;
        try {
            setPhoto(await fileToNormalizedDataUrl(f));
            setKind("photo");
            setStep("where");
        } catch {
            toast.error("That picture couldn't be read. Try again.");
        }
    };
    const onFilePicked = (f: File | undefined) => {
        if (!f) return;
        if (f.size > FIELD_ADD_MAX_BYTES) {
            toast.error("That file is over 10 MB. Take a photo of it instead.");
            return;
        }
        setFile(f);
        setKind(f.type.startsWith("image/") ? "photo" : "file");
        setStep("where");
    };

    const tile = (t: FieldTarget) => {
        const p = PLACES[t];
        const picked = target === t;
        const sub = t === "device" && picked && pickedDevice ? deviceName(pickedDevice) : p.sub;
        return (
            <button
                key={t}
                type="button"
                data-testid={`field-add-tile-${t}`}
                aria-pressed={picked}
                onClick={() => {
                    if (t === "device") {
                        setTarget("device");
                        setStep("device");
                    } else {
                        setTarget(t);
                        setAssetId(null);
                    }
                }}
                className={cn(
                    "flex min-h-[104px] flex-col items-center justify-center gap-1.5 rounded-2xl border-2 px-2 py-3 text-center transition-colors",
                    picked ? cn("border-[3px]", p.on) : "border-border bg-card",
                    p.ink,
                )}
            >
                <p.Icon className="h-8 w-8" aria-hidden />
                <b className="text-[17px] leading-tight text-foreground">{p.label}</b>
                <span className="text-xs leading-tight text-foreground/70">{sub}</span>
            </button>
        );
    };

    const officeSwitch = (
        <button
            type="button"
            role="switch"
            aria-checked={needsOffice}
            data-testid="field-add-needs-office"
            onClick={() => setNeedsOffice((v) => !v)}
            className="flex w-full items-center gap-3 rounded-xl border-2 border-border px-3 py-2.5 text-left"
        >
            <span
                className={cn(
                    "relative h-6 w-11 shrink-0 rounded-full transition-colors",
                    needsOffice ? "bg-red-700" : "bg-muted-foreground/30",
                )}
            >
                <span
                    className={cn(
                        "absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white transition-all",
                        needsOffice ? "left-[23px]" : "left-[3px]",
                    )}
                />
            </span>
            <span className="text-sm leading-snug">
                <b>Office needs to see this</b>
                <br />
                <span className="text-xs text-foreground/70">
                    Shows on the office's From the field list until someone marks it read
                </span>
            </span>
        </button>
    );

    const tiles = helperOnly ? (
        <div className="space-y-2">
            <div className="grid grid-cols-1">{tile("job")}</div>
            <p className="text-sm text-foreground/70">
                Helpers add to this job only. Your tech or the office can move it.
            </p>
        </div>
    ) : (
        <div className="grid grid-cols-2 gap-2.5">
            {(["job", "device", "location", "customer"] as const).filter((t) => targets.includes(t)).map(tile)}
        </div>
    );

    const saveButton = (label: string, disabled = false) => (
        <button
            type="button"
            data-testid="field-add-save"
            disabled={disabled || busy}
            onClick={() => (target === "device" && !assetId ? setStep("device") : void commit(target, assetId))}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-green-700 text-[17px] font-bold text-white disabled:opacity-50"
        >
            {busy && <Loader2 className="h-5 w-5 animate-spin" />}
            {label}
        </button>
    );

    const header = (title: string, topic: keyof typeof FIELD_ADD_HELP) => (
        <div className="flex items-center justify-between gap-2">
            <h3 className="text-[22px] font-bold leading-tight">{title}</h3>
            <InfoButton topic={FIELD_ADD_HELP[topic]} />
        </div>
    );

    let content: React.ReactNode = null;

    if (step === "what") {
        const big = (k: FieldAddKind, Icon: typeof Camera, small: string, onClick: () => void) => (
            <button
                type="button"
                data-testid={`field-add-${k}`}
                onClick={onClick}
                className="flex flex-col items-center gap-1.5 rounded-2xl border-2 border-border bg-card px-1.5 py-4 text-base font-bold"
            >
                <Icon className="h-9 w-9" aria-hidden />
                {KIND_WORD[k]}
                <small className="text-xs font-normal text-foreground/70">{small}</small>
            </button>
        );
        content = (
            <>
                {header("What are you adding?", "what")}
                <div className="grid grid-cols-3 gap-2.5">
                    {big("photo", Camera, "Take or pick", () => cameraRef.current?.click())}
                    {big("note", StickyNote, "Type or talk", () => {
                        setKind("note");
                        setStep("note");
                    })}
                    {big("file", FileCheck2, "Form, permit, PDF", () => fileRef.current?.click())}
                </div>
                <InsideOnlyLine />
                {mine.length > 0 && (
                    <button
                        type="button"
                        data-testid="field-add-mine-link"
                        onClick={() => setStep("mine")}
                        className="flex h-12 w-full items-center justify-between rounded-xl border-2 border-border px-3 text-left text-[15px] font-bold"
                    >
                        <span>Your adds today ({mine.length}): fix, move or remove</span>
                        <ChevronRight className="h-5 w-5 text-foreground/50" aria-hidden />
                    </button>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    className="h-12 w-full rounded-xl border-2 border-border text-base font-bold"
                >
                    Cancel
                </button>
            </>
        );
    } else if (step === "mine") {
        content = (
            <>
                <button
                    type="button"
                    onClick={() => setStep("what")}
                    className="inline-flex items-center gap-1 self-start rounded-xl border-2 border-border px-3 py-1.5 text-[15px] font-bold text-primary"
                >
                    <ChevronLeft className="h-4 w-4" /> Back
                </button>
                <h3 className="text-[22px] font-bold leading-tight">Your adds today</h3>
                <p className="text-sm text-foreground/70">
                    You can fix, move or remove these until the end of today. After that, ask the office.
                </p>
                <div className="space-y-2">
                    {mine.map((item) => (
                        <MineRow
                            key={item.id}
                            item={item}
                            onMove={() => {
                                setSaved({ queued: false, id: item.id, kindPath: kindPathOf(item) });
                                setKind(item.kind);
                                setTarget(item.target);
                                setAssetId(item.assetId);
                                setMoving(true);
                                setStep("where");
                            }}
                            onChanged={() => void qc.invalidateQueries({ queryKey: fieldAddJobKey(jobId) })}
                        />
                    ))}
                </div>
            </>
        );
    } else if (step === "note") {
        content = (
            <>
                {header("Note", "note")}
                <textarea
                    data-testid="field-add-note-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={4000}
                    autoFocus
                    rows={4}
                    placeholder="What should the office or the next tech know?"
                    className="min-h-[96px] w-full rounded-xl border-2 border-border bg-card p-3 text-base"
                />
                <div className="flex items-center gap-2 text-sm text-foreground/70">
                    <Mic className="h-4 w-4 shrink-0" aria-hidden />
                    To talk, tap the
                    <span className="rounded-md border px-2 py-0.5 font-bold text-foreground">mic</span>
                    key on the keyboard
                </div>
                {presetDevice ? <PresetDeviceLine device={presetDevice} /> : tiles}
                {officeSwitch}
                {saveButton("Save note", body.trim().length === 0)}
            </>
        );
    } else if (step === "where") {
        content = (
            <>
                {header(moving ? "Move it where?" : "Where does this go?", "where")}
                {!moving && <Preview kind={kind} photo={photo} file={file} />}
                {presetDevice && !moving ? <PresetDeviceLine device={presetDevice} /> : tiles}
                {!moving && officeSwitch}
                {moving ? (
                    <button
                        type="button"
                        onClick={() => {
                            setMoving(false);
                            setStep("saved");
                        }}
                        className="h-12 w-full rounded-xl border-2 border-border text-base font-bold"
                    >
                        Keep it where it is
                    </button>
                ) : null}
                {saveButton(moving ? "Move it here" : "Save")}
            </>
        );
    } else if (step === "device") {
        content = (
            <>
                <button
                    type="button"
                    data-testid="field-add-back"
                    onClick={() => {
                        setStep(kind === "note" && !moving ? "note" : "where");
                        if (!assetId) setTarget("job");
                    }}
                    className="inline-flex items-center gap-1 self-start rounded-xl border-2 border-border px-3 py-1.5 text-[15px] font-bold text-primary"
                >
                    <ChevronLeft className="h-4 w-4" /> Back
                </button>
                {header("Which device?", "device")}
                <p className="text-sm text-foreground/70">Match the serial number to the tag in front of you.</p>
                {devices.length === 0 ? (
                    <p className="rounded-xl border-2 border-dashed border-border p-4 text-sm">
                        There are no devices on this job. Tap Back and pick This job or This location.
                    </p>
                ) : (
                    <div className="space-y-2" data-testid="field-add-devices">
                        {devices.map((d) => {
                            const on = assetId === d.assetId;
                            return (
                                <button
                                    key={d.assetId}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                        setAssetId(d.assetId);
                                        setTarget("device");
                                        // Tap one and it saves (mockup 3b) — a note waits for its words.
                                        if (kind === "note" && body.trim().length === 0) setStep("note");
                                        else void commit("device", d.assetId);
                                    }}
                                    className={cn(
                                        "flex w-full items-center gap-3 rounded-xl border-2 px-3 py-3.5 text-left",
                                        PLACES.device.ink,
                                        on ? cn("border-[3px]", PLACES.device.on) : "border-border bg-card",
                                    )}
                                >
                                    <PLACES.device.Icon className="h-7 w-7 shrink-0" aria-hidden />
                                    <span className="min-w-0 flex-1">
                                        <b className="block text-[17px] leading-tight text-foreground">{d.label}</b>
                                        <span className="block text-[13px] text-foreground/70">
                                            {[d.serial ? `SN ${d.serial}` : "No serial on file", d.location]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </span>
                                    </span>
                                    <ChevronRight className="h-6 w-6 shrink-0 text-foreground/50" aria-hidden />
                                </button>
                            );
                        })}
                    </div>
                )}
            </>
        );
    } else if (step === "saved") {
        const item = {
            target,
            deviceLabel: pickedDevice?.label ?? null,
            deviceSerial: pickedDevice?.serial ?? null,
            deviceLocation: pickedDevice?.location ?? null,
        };
        const p = PLACES[target];
        content = (
            <div className="flex flex-col items-center gap-3.5 py-2 text-center" data-testid="field-add-saved">
                <div className="grid h-[76px] w-[76px] place-items-center rounded-full bg-green-700 text-white">
                    <Check className="h-11 w-11" strokeWidth={3} aria-hidden />
                </div>
                <h3 className="text-[26px] font-bold">{KIND_WORD[kind]} saved</h3>
                <div className="flex w-full flex-col gap-2 text-left">
                    <div className={cn("flex items-start gap-2 rounded-xl border-2 px-3 py-2.5 text-sm", p.on, p.ink)}>
                        <p.Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                        <span className="text-foreground" data-testid="field-add-where-line">
                            {whereLine(item)}
                        </span>
                    </div>
                    <div className="flex items-start gap-2 rounded-xl border px-3 py-2.5 text-sm">
                        <Eye className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                        <span>{whoSees(target)}</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2.5 text-sm font-bold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                        <Lock className="h-5 w-5 shrink-0" aria-hidden />
                        The customer never sees it.
                    </div>
                    {saved?.queued && (
                        <p className="text-sm text-foreground/70">
                            No signal right now. It's saved on this iPad and sends by itself.
                        </p>
                    )}
                </div>
                <div className="flex w-full gap-2">
                    {saved?.id && !helperOnly && (
                        <button
                            type="button"
                            data-testid="field-add-move"
                            onClick={() => {
                                if (closeTimer.current) window.clearTimeout(closeTimer.current);
                                setMoving(true);
                                setStep("where");
                            }}
                            className="h-14 flex-1 rounded-xl border-2 border-border text-[17px] font-bold"
                        >
                            Move it
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-14 flex-1 rounded-xl bg-green-700 text-[17px] font-bold text-white"
                    >
                        Done
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" data-testid="field-add-sheet">
            <div className="absolute inset-0 bg-black/45" onClick={step === "saved" ? onClose : undefined} />
            <div
                role="dialog"
                aria-label="Add a photo, note or file"
                className="relative mx-auto flex max-h-[92vh] w-full max-w-xl flex-col gap-3 overflow-y-auto rounded-t-2xl border-t bg-card p-4 pb-6 shadow-2xl"
            >
                {content}
                <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                        void onPhotoPicked(e.target.files?.[0]);
                        e.target.value = "";
                    }}
                />
                <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => {
                        onFilePicked(e.target.files?.[0]);
                        e.target.value = "";
                    }}
                />
            </div>
        </div>
    );
}

function Preview({ kind, photo, file }: { kind: FieldAddKind; photo: string | null; file: File | null }) {
    if (photo) {
        return <img src={photo} alt="Your photo" className="h-24 w-full rounded-xl border object-cover" />;
    }
    return (
        <div className="grid h-24 place-items-center rounded-xl border border-dashed text-sm text-foreground/70">
            {kind === "file" && file ? file.name : "Your photo"}
        </div>
    );
}

function PresetDeviceLine({ device }: { device: PickerDevice }) {
    const p = PLACES.device;
    return (
        <div
            className={cn("flex items-center gap-3 rounded-xl border-[3px] px-3 py-3", p.on, p.ink)}
            data-testid="field-add-preset-device"
        >
            <p.Icon className="h-7 w-7 shrink-0" aria-hidden />
            <span className="min-w-0">
                <span className="block text-xs font-bold uppercase tracking-wide">Goes on this device</span>
                <b className="block text-[17px] leading-tight text-foreground">{device.label}</b>
                <span className="block text-[13px] text-foreground/70">
                    {[device.serial ? `SN ${device.serial}` : null, device.location].filter(Boolean).join(" · ")}
                </span>
            </span>
        </div>
    );
}

function MineRow({ item, onMove, onChanged }: { item: FieldAddItem; onMove: () => void; onChanged: () => void }) {
    const [editing, setEditing] = useState(false);
    const [words, setWords] = useState(item.body ?? "");
    const [armed, setArmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const run = async (fn: () => Promise<unknown>, done: string) => {
        setBusy(true);
        try {
            await fn();
            toast.success(done);
            onChanged();
        } catch (e: unknown) {
            const err = e as { body?: { message?: string } };
            toast.error(err?.body?.message ?? "Couldn't change it. Try again.");
        } finally {
            setBusy(false);
        }
    };
    const what = item.kind === "note" ? (item.body ?? "") : item.kind === "photo" ? "Photo" : (item.fileName ?? "File");
    return (
        <div className="space-y-2 rounded-xl border-2 border-border p-3" data-testid="field-add-mine-row">
            <div className="flex flex-wrap items-center gap-2">
                <PlacePill item={item} />
                <span className="text-xs text-foreground/70">{shortTime(item.createdAt)}</span>
            </div>
            {editing ? (
                <textarea
                    value={words}
                    onChange={(e) => setWords(e.target.value)}
                    rows={3}
                    maxLength={4000}
                    className="w-full rounded-lg border-2 border-border bg-card p-2 text-base"
                />
            ) : (
                <p className="line-clamp-3 text-sm">{what}</p>
            )}
            <div className="flex flex-wrap gap-2">
                {item.kind === "note" &&
                    (editing ? (
                        <button
                            type="button"
                            disabled={busy || words.trim().length === 0}
                            onClick={() =>
                                void run(
                                    () => api.patch(`/api/field-adds/notes/${item.id}`, { body: words }),
                                    "Note fixed",
                                ).then(() => setEditing(false))
                            }
                            className="h-11 rounded-lg bg-green-700 px-4 text-sm font-bold text-white disabled:opacity-50"
                        >
                            Save words
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setEditing(true)}
                            className="h-11 rounded-lg border-2 border-border px-4 text-sm font-bold"
                        >
                            Fix the words
                        </button>
                    ))}
                <button
                    type="button"
                    onClick={onMove}
                    className="h-11 rounded-lg border-2 border-border px-4 text-sm font-bold"
                >
                    Move it
                </button>
                <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                        armed ? void run(() => removeFieldAdd(kindPathOf(item), item.id), "Removed") : setArmed(true)
                    }
                    className={cn(
                        "h-11 rounded-lg border-2 px-4 text-sm font-bold",
                        armed ? "border-red-700 bg-red-700 text-white" : "border-border text-red-700",
                    )}
                >
                    {armed ? "Tap again to remove" : "Remove"}
                </button>
            </div>
        </div>
    );
}
